import { useMemo } from 'react';

import { useEmployeeEditingCtx } from '@/app/(app)/settings/employees/[id]/provider';
import { ToggleButtonWithCheckbox } from '@/components/atoms/toggle-button';
import { useApiV1Ctx } from '@/components/providers/v1-api';
import { useOfficeCtx } from '@/components/providers/v1-office-provider';
import { Button } from '@/components/ui/button';

export function EmployeeSkillsAndRegions() {
  return (
    <div className='flex w-full flex-col gap-14'>
      <EmployeeRegions />
      <EmployeeSkills />
    </div>
  );
}

function EmployeeRegions() {
  const { employee, update } = useEmployeeEditingCtx();
  const {
    office: { regions },
  } = useOfficeCtx();
  const allSelected = useMemo(() => employee.regions_ids.length === regions.length, [employee.regions_ids, regions]);

  const toggleRegion = (id: string) => {
    update({
      regions_ids: employee.regions_ids.includes(id)
        ? employee.regions_ids?.filter((regionId) => regionId !== id)
        : [...employee.regions_ids, id],
    });
  };

  const toggleAll = () => {
    if (allSelected) {
      update({ regions_ids: [] });
    } else {
      update({ regions_ids: regions.map((r) => r.id) });
    }
  };

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex items-center justify-between gap-2'>
        <h2 className='text-heading-md'>Regions</h2>
        <Button variant='link' size='sm' onClick={toggleAll}>
          {allSelected ? 'Deselect All' : 'Select All'}
        </Button>
      </div>
      <div className='grid grid-cols-2 gap-3 gap-y-4'>
        {regions.map((region) => (
          <ToggleButtonWithCheckbox
            key={region.id}
            selected={employee.regions_ids.includes(region.id)}
            toggle={() => toggleRegion(region.id)}
          >
            {region.name}
          </ToggleButtonWithCheckbox>
        ))}
      </div>
    </div>
  );
}

function EmployeeSkills() {
  const { api } = useApiV1Ctx();
  const { employee, update } = useEmployeeEditingCtx();
  const {
    office: { skills },
  } = useOfficeCtx();
  const allSelected = useMemo(() => employee.skills_ids.length === skills.length, [employee.skills_ids, skills]);

  const toggleSkill = (id: string) => {
    update({
      skills_ids: employee.skills_ids.includes(id)
        ? employee.skills_ids.filter((skillId) => skillId !== id)
        : [...employee.skills_ids, id],
    });
  };

  const toggleAll = () => {
    if (allSelected) {
      update({ skills_ids: [] });
    } else {
      update({ skills_ids: skills.map((s) => s.id) });
    }
  };

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex items-center justify-between gap-2'>
        <h2 className='text-heading-md'>Skills</h2>
        <Button variant='link' size='sm' onClick={toggleAll}>
          {allSelected ? 'Deselect All' : 'Select All'}
        </Button>
      </div>
      <div className='grid grid-cols-2 gap-3 gap-y-4'>
        {skills.map((skill) => (
          <ToggleButtonWithCheckbox
            key={skill.id}
            selected={employee.skills_ids.includes(skill.id)}
            toggle={() => toggleSkill(skill.id)}
          >
            {skill.name}
          </ToggleButtonWithCheckbox>
        ))}
      </div>
    </div>
  );
}
